import { z } from 'zod'
import { monosanFetch } from '@/shared/api/monosanFetch'
import { ApiError } from '@/shared/api/errors'
import { tokenStore } from '@/features/auth/model/tokenStore'

const clientId = import.meta.env.VITE_OAUTH_CLIENT_ID ?? 'nativeweb'
const basicAuth = `Basic ${btoa(`${clientId}:`)}`

export type LoginResult =
  | { kind: 'authenticated' }
  | { kind: 'mfa_required'; mfaToken: string }

const loginResponseSchema = z.object({
  accessToken: z.string(),
  expiresIn: z.number(),
})

const mfaRequiredSchema = z.object({
  mfaToken: z.string(),
})

const mfaChallengeResponseSchema = z.object({
  obfuscatedPhoneNumber: z.string(),
})

export const login = async (
  username: string,
  password: string
): Promise<LoginResult> => {
  try {
    const { accessToken, expiresIn } = await monosanFetch('/api/auth/login', {
      method: 'POST',
      body: { username, password },
      schema: loginResponseSchema,
      skipAuth: true,
      requiresCookie: true,
    })
    tokenStore.set({ accessToken, expiresAt: Date.now() + expiresIn * 1000 })
    return { kind: 'authenticated' }
  } catch (err) {
    if (err instanceof ApiError && err.status === 403) {
      const parsed = mfaRequiredSchema.safeParse(err.body)
      if (parsed.success) {
        return { kind: 'mfa_required', mfaToken: parsed.data.mfaToken }
      }
    }
    throw err
  }
}

export const submitMfa = async (
  mfaToken: string,
  otp: string
): Promise<void> => {
  const { accessToken, expiresIn } = await monosanFetch('/api/auth/mfa', {
    method: 'POST',
    body: { mfaToken, otp },
    schema: loginResponseSchema,
    skipAuth: true,
    requiresCookie: true,
  })
  tokenStore.set({ accessToken, expiresAt: Date.now() + expiresIn * 1000 })
}

export const requestMfaChallenge = async (
  mfaToken: string
): Promise<{ obfuscatedPhoneNumber: string }> => {
  return monosanFetch('/api/mfa/challenge', {
    method: 'POST',
    headers: { Authorization: basicAuth },
    body: { mfaToken, challengeType: 'otp' },
    schema: mfaChallengeResponseSchema,
    skipAuth: true,
  })
}
